import React, { useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import Plot from 'react-plotly.js';
import {
  IoDocumentText,
  IoTime,
  IoCheckmarkCircle,
  IoCalendar,
  IoAdd,
} from 'react-icons/io5';
import DemoBanner from '@/components/DemoBanner';
import { API } from '@/utils/api.utils';
import api from '@/utils/api.utils';
import './Progress.css';

interface University {
  university_id: number;
  user_id: number;
  status: string;
  is_suggested: boolean;
  id: number;
  university_name: string;
}

interface UniversityResponse {
  IN_PROGRESS: {
    universities: University[];
    count: number;
  };
  PENDING: {
    universities: University[];
    count: number;
  };
  COMPLETED: {
    universities: University[];
    count: number;
  };
}

interface ApplicationProgress {
  id: number;
  name: string;
  deadline: string;
  status: string;
  completion: number;
  nextSteps?: string[];
}

const Progress: React.FC = () => {
  // Fetch user universities
  const { data: universitiesData, isLoading } = useQuery<UniversityResponse>({
    queryKey: ['user-universities'],
    queryFn: async () => {
      const response = await api.get(API.USER_UNIVERSITIES);
      return response.data;
    },
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    retry: false, // Don't retry on failure
    enabled: false, // Disabled in demo mode - no real auth
  });

  // Transform universities to application progress format
  const applicationProgress: ApplicationProgress[] = useMemo(() => {
    if (!universitiesData) return [];

    const allUniversities = [
      ...universitiesData.IN_PROGRESS.universities,
      ...universitiesData.PENDING.universities,
      ...universitiesData.COMPLETED.universities,
    ];

    return allUniversities.map((univ, index) => ({
      id: univ.university_id || index,
      name: univ.university_name,
      deadline: 'Jan 5, 2025', // Mock deadline
      status: univ.status,
      completion: univ.status === 'COMPLETED' ? 100 : univ.status === 'IN_PROGRESS' ? 75 : 50,
      nextSteps: getNextSteps(univ.university_name, univ.status),
    }));
  }, [universitiesData]);

  // Mock analytics data - memoized
  const analyticsData = useMemo(() => ({
    avgCompletionTime: '12 days',
    successRate: '87%',
    estimatedDecisions: 'Mar 15',
  }), []);

  // Memoize chart data to prevent re-creation on every render
  const monthlyProgressChartData = useMemo(() => [
    {
      type: 'scatter',
      mode: 'lines',
      name: 'Applications Started',
      x: ['Sep', 'Oct', 'Nov', 'Dec', 'Jan'],
      y: [2, 4, 6, 7, 8],
      line: { color: '#6B7AE8', width: 3 },
      fill: 'tozeroy',
      fillcolor: 'rgba(107, 122, 232, 0.1)',
    } as any,
    {
      type: 'scatter',
      mode: 'lines',
      name: 'Applications Submitted',
      x: ['Sep', 'Oct', 'Nov', 'Dec', 'Jan'],
      y: [0, 1, 2, 4, 5],
      line: { color: '#10B981', width: 3 },
    } as any,
  ], []);

  const monthlyProgressChartLayout = useMemo(() => ({
    xaxis: { title: { text: 'Month' } },
    yaxis: { title: { text: 'Number of Applications' } },
    margin: { t: 20, r: 20, b: 60, l: 60 },
    plot_bgcolor: '#ffffff',
    paper_bgcolor: '#ffffff',
    showlegend: true,
    legend: { x: 0, y: 1.1, orientation: 'h' },
    height: 400,
  } as any), []);

  const schoolTypeChartData = useMemo(() => [
    {
      type: 'pie',
      labels: ['Reach Schools', 'Target Schools', 'Safety Schools'],
      values: [3, 3, 2],
      marker: {
        colors: ['#EF4444', '#6B7AE8', '#10B981'],
      },
      textinfo: 'label+percent',
      textposition: 'inside',
    } as any,
  ], []);

  const schoolTypeChartLayout = useMemo(() => ({
    margin: { t: 20, r: 20, b: 20, l: 20 },
    plot_bgcolor: '#ffffff',
    paper_bgcolor: '#ffffff',
    showlegend: true,
    legend: { x: 0, y: -0.1, orientation: 'h' },
    height: 400,
  } as any), []);

  const plotConfig = useMemo(() => ({
    responsive: true,
    displayModeBar: false,
    displaylogo: false,
  }), []);

  const getStatusBadgeColor = useCallback((status: string): string => {
    switch (status) {
      case 'COMPLETED':
      case 'SUBMITTED':
        return 'status-badge-green';
      case 'IN_PROGRESS':
        return 'status-badge-yellow';
      case 'PENDING':
        return 'status-badge-gray';
      default:
        return 'status-badge-gray';
    }
  }, []);

  const getStatusLabel = useCallback((status: string): string => {
    switch (status) {
      case 'COMPLETED':
        return 'Submitted';
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'PENDING':
        return 'Pending';
      default:
        return status;
    }
  }, []);

  const getNextSteps = useCallback((universityName: string, status: string): string[] => {
    // Generate context-aware next steps based on status
    const baseSteps = [
      'Attend at least 1 demonstrated interest opportunity',
      'Finish your 2 remaining supplemental essays',
      'Email your admissions rep',
      'Apply for financial aid before Dec 1',
    ];

    if (status === 'COMPLETED') {
      return [
        'Follow up with admissions office',
        'Prepare for potential interview',
        'Continue demonstrating interest',
      ];
    }

    if (status === 'IN_PROGRESS') {
      return [
        'Attend at least 1 demonstrated interest opportunity',
        'Finish your 2 remaining supplemental essays',
        'Email your admissions rep',
        'Apply for financial aid before Dec 1',
      ];
    }

    return baseSteps;
  }, []);

  return (
    <div className="progress-page dark:bg-gray-900">
      <div className="progress-container">
        <DemoBanner />
        {/* Application Analytics */}
        <div className="analytics-section">
          <div className="analytics-card dark:bg-gray-800 dark:border-gray-700">
            <h2 className="card-title dark:text-gray-100">Application Analytics</h2>

            <div className="analytics-charts-container">
              <div className="chart-wrapper dark:bg-gray-800 dark:border-gray-700">
                <h3 className="chart-title dark:text-gray-200">Monthly Progress Overview</h3>
                <Plot
                  data={monthlyProgressChartData}
                  layout={monthlyProgressChartLayout}
                  config={plotConfig}
                  style={{ width: '100%', height: '400px' }}
                  useResizeHandler={false}
                />
              </div>

              <div className="chart-wrapper dark:bg-gray-800 dark:border-gray-700">
                <h3 className="chart-title dark:text-gray-200">Applications by School Type</h3>
                <Plot
                  data={schoolTypeChartData}
                  layout={schoolTypeChartLayout}
                  config={plotConfig}
                  style={{ width: '100%', height: '400px' }}
                  useResizeHandler={false}
                />
              </div>
            </div>

            <div className="analytics-grid">
              <div className="analytics-stat dark:bg-gray-800 dark:border-gray-700">
                <div className="stat-header">
                  <span className="stat-label dark:text-gray-300">Avg. Completion Time</span>
                  <IoTime className="stat-icon dark:text-gray-400" />
                </div>
                <p className="stat-value dark:text-gray-100">{analyticsData.avgCompletionTime}</p>
                <p className="stat-subtext dark:text-gray-400">Per application</p>
              </div>

              <div className="analytics-stat dark:bg-gray-800 dark:border-gray-700">
                <div className="stat-header">
                  <span className="stat-label dark:text-gray-300">Success Rate</span>
                  <IoCheckmarkCircle className="stat-icon dark:text-gray-400" />
                </div>
                <p className="stat-value dark:text-gray-100">{analyticsData.successRate}</p>
                <p className="stat-subtext dark:text-gray-400">Profile completeness</p>
              </div>

              <div className="analytics-stat dark:bg-gray-800 dark:border-gray-700">
                <div className="stat-header">
                  <span className="stat-label dark:text-gray-300">Estimated Decisions</span>
                  <IoCalendar className="stat-icon dark:text-gray-400" />
                </div>
                <p className="stat-value dark:text-gray-100">{analyticsData.estimatedDecisions}</p>
                <p className="stat-subtext dark:text-gray-400">Average decision date</p>
              </div>
            </div>
          </div>
        </div>

        {/* Application Progress */}
        <div className="application-progress-card dark:bg-gray-800 dark:border-gray-700">
          <div className="card-header">
            <h2 className="card-title dark:text-gray-100">Application Progress</h2>
            <select className="progress-filter dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100">
              <option>All Applications</option>
              <option>In Progress</option>
              <option>Submitted</option>
              <option>Under Review</option>
            </select>
          </div>

          <div className="applications-list">
            {isLoading ? (
              <div className="loading-state dark:text-gray-400">Loading applications...</div>
            ) : applicationProgress.length > 0 ? (
              applicationProgress.map((app) => (
                <div key={app.id} className="application-item dark:bg-gray-800 dark:border-gray-700">
                  <div className="application-header">
                    <div className="application-info">
                      <div className="application-icon dark:bg-blue-900/30">
                        <IoDocumentText className="application-icon-svg dark:text-blue-400" />
                      </div>
                      <div>
                        <h3 className="application-name dark:text-gray-100">{app.name}</h3>
                        <p className="application-deadline dark:text-gray-400">Regular Decision • Deadline: {app.deadline}</p>
                      </div>
                    </div>
                    <span className={`status-badge ${getStatusBadgeColor(app.status)}`}>
                      {getStatusLabel(app.status)}
                    </span>
                  </div>
                  <div className="application-progress-bar">
                    <div className="progress-bar-bg dark:bg-gray-700">
                      <div
                        className="progress-bar-fill"
                        style={{ width: `${app.completion}%` }}
                      ></div>
                    </div>
                    <span className="progress-percentage dark:text-gray-200">{app.completion}%</span>
                  </div>
                  <div className="application-status-icons dark:text-gray-300">
                    <span className="status-icon-item">
                      <IoCheckmarkCircle className="status-icon-check" />
                      Common App Complete
                    </span>
                    <span className="status-icon-item">
                      <IoCheckmarkCircle className="status-icon-check" />
                      Essays Submitted
                    </span>
                    <span className="status-icon-item">
                      <IoTime className="status-icon-pending" />
                      Recommendations Pending
                    </span>
                  </div>
                  
                  {/* Recommended Next Steps */}
                  <div className="next-steps-container dark:bg-gray-700 dark:border-l-blue-500">
                    <h4 className="next-steps-title dark:text-gray-100">Recommended Next Steps</h4>
                    <ul className="next-steps-list">
                      {app.nextSteps?.map((step, index) => (
                        <li key={index} className="next-step-item dark:text-gray-300">
                          {step}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state dark:text-gray-400">No applications yet. Start by adding a university to your list.</div>
            )}
          </div>

          <button className="add-application-button dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:border-blue-500 dark:hover:text-blue-400">
            <IoAdd className="add-icon" />
            <span>Add New Application</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Progress;

